/**
 * StatusBadge component - displays audit processing status
 */

import { cloneElement } from "react";
import { useTranslation } from "react-i18next";
import type { AuditStatus } from "@/types";
import type { AuditProcessingStage } from "@vardic/shared/types";
import { Badge } from "./Badge";
import { statusBadgeTones } from "./badge-colors";
import { Tooltip, TooltipPopup, TooltipTrigger } from "@/components/ui/tooltip";
import { AuditStageIndicator } from "./AuditStageIndicator";
import { resolveStageLabel } from "./audit-stage-label";

interface StatusBadgeProps {
  status: AuditStatus;
  errorMessage?: string | null;
  /** Current pipeline stage; only consulted when status is PROCESSING. */
  currentStep?: AuditProcessingStage | null;
  /** Processing lease start, for the tooltip's elapsed-time readout. */
  processingStartedAt?: string | null;
}

export function StatusBadge({
  status,
  errorMessage,
  currentStep,
  processingStartedAt,
}: StatusBadgeProps) {
  const { t } = useTranslation(["history"]);
  const label = t(`history:status.${status}`);

  const badge = (
    <Badge variant="status" toneClassName={statusBadgeTones[status]}>
      {label}
      {status === "PROCESSING" && (
        <svg
          className="ml-2 h-3 w-3 animate-spin"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          >
          </circle>
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          >
          </path>
        </svg>
      )}
    </Badge>
  );

  // Surface the failure reason on hover/focus via a Coss Tooltip — only for a
  // FAILED audit that actually carries a message, so a stale error never haunts
  // the next successful retry.
  if (status === "FAILED" && errorMessage) {
    return (
      <Tooltip>
        <TooltipTrigger render={cloneElement(badge, { tabIndex: 0 })} />
        <TooltipPopup data-test="status-badge-error">
          {errorMessage}
        </TooltipPopup>
      </Tooltip>
    );
  }

  // Surface the current pipeline stage + elapsed time on hover/focus via the
  // same tooltip mechanism as the FAILED case above — keeps the status
  // column at exactly one visual element per row instead of stacking a
  // second line under the badge, which used to render inconsistently since
  // currentStep can be null for stretches of a PROCESSING audit (see
  // AuditStageIndicator's header comment).
  const stageLabel = status === "PROCESSING"
    ? resolveStageLabel(currentStep, t)
    : null;
  if (stageLabel) {
    return (
      <Tooltip>
        <TooltipTrigger render={cloneElement(badge, { tabIndex: 0 })} />
        <TooltipPopup data-test="status-badge-stage">
          <AuditStageIndicator
            currentStep={currentStep}
            processingStartedAt={processingStartedAt}
          />
        </TooltipPopup>
      </Tooltip>
    );
  }

  return badge;
}
